import React, { useEffect, useState } from 'react';
import { Card, Table, Button, Modal, Form, Input, Select, Space, Tag, message, Typography, Descriptions, Steps, Popconfirm } from 'antd';
import { SettingOutlined, PlusOutlined, DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { workflowApi } from '../../services/api';
import useAuthStore from '../../store/authStore';

const { Title, Text } = Typography;
const { Option } = Select;

const STAGE_TYPES = [
  { value: 'approval', label: '审批节点', color: 'blue' },
  { value: 'manual', label: '手动操作', color: 'orange' },
  { value: 'auto', label: '自动执行', color: 'green' },
];

const ROLES = [
  { value: 'admin', label: '总经理' },
  { value: 'manager', label: '销售主管' },
  { value: 'sales', label: '销售' },
];

const ACTIONS = [
  { value: 'signContract', label: '自动签署合同' },
  { value: 'confirmQuotation', label: '确认报价成交' },
  { value: 'adjustStock', label: '执行库存调整' },
  { value: 'markOrdered', label: '标记已下单' },
];

const NOTIFY_CHANNELS = [
  { value: 'wecom', label: '企业微信' },
  { value: 'email', label: '邮件' },
  { value: 'system', label: '站内信' },
];

export default function WorkflowConfig() {
  const user = useAuthStore((s) => s.user);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [activeTemplate, setActiveTemplate] = useState(null);
  const [stages, setStages] = useState([]);
  const [templateName, setTemplateName] = useState('');

  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const data = await workflowApi.templates();
      setTemplates(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchTemplates(); }, []);

  const openEditor = (record) => {
    setActiveTemplate(record);
    setTemplateName(record.name);
    try {
      const parsed = typeof record.stages === 'string' ? JSON.parse(record.stages) : record.stages;
      setStages(parsed.map((s, i) => ({ ...s, key: i })));
    } catch {
      setStages([]);
    }
    setEditModalOpen(true);
  };

  const addStage = () => {
    setStages((prev) => [
      ...prev,
      { key: Date.now(), order: prev.length + 1, name: '', type: 'approval', role: 'manager', notify: '', action: '' },
    ]);
  };

  const removeStage = (key) => {
    setStages((prev) => prev.filter((s) => s.key !== key).map((s, i) => ({ ...s, order: i + 1 })));
  };

  const updateStage = (key, field, value) => {
    setStages((prev) => prev.map((s) => (s.key === key ? { ...s, [field]: value } : s)));
  };

  const handleSave = async () => {
    if (!templateName.trim()) {
      message.error('请填写模板名称');
      return;
    }
    const invalidStage = stages.find((s) => !s.name.trim());
    if (invalidStage) {
      message.error('每个阶段都必须填写名称');
      return;
    }

    const stagesData = stages.map(({ key, ...rest }) => rest);
    try {
      await workflowApi.updateTemplate(activeTemplate.code, {
        name: templateName,
        stages: stagesData,
      });
      message.success('模板已保存');
      setEditModalOpen(false);
      fetchTemplates();
    } catch (e) {
      message.error(e?.error || '保存失败');
    }
  };

  const columns = [
    { title: '模板名称', dataIndex: 'name', key: 'name', render: (v) => <Text strong>{v}</Text> },
    { title: '编码', dataIndex: 'code', key: 'code', render: (v) => <Tag>{v}</Tag> },
    { title: '所属模块', dataIndex: 'module', key: 'module', render: (v) => {
      const map = { contract: '合同', quotation: '报价', stock: '库存', purchase: '采购' };
      return map[v] || v;
    }},
    {
      title: '阶段数', key: 'stageCount',
      render: (_, r) => {
        try {
          const s = typeof r.stages === 'string' ? JSON.parse(r.stages) : r.stages;
          return s.length;
        } catch { return 0; }
      },
    },
    {
      title: '阶段预览', key: 'preview',
      render: (_, r) => {
        try {
          const s = typeof r.stages === 'string' ? JSON.parse(r.stages) : r.stages;
          return (
            <Steps size="small" current={-1} items={s.map((st) => ({
              title: <span style={{ fontSize: 12 }}>{st.name}</span>,
              status: 'wait',
            }))} />
          );
        } catch { return '—'; }
      },
    },
    {
      title: '操作', key: 'actions',
      render: (_, r) => (
        <Button size="small" icon={<EditOutlined />} onClick={() => openEditor(r)}>配置</Button>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Title level={4} style={{ margin: 0 }}>工作流配置</Title>
      </div>

      <Card>
        <Table
          columns={columns}
          dataSource={templates}
          rowKey="id"
          loading={loading}
          pagination={false}
        />
      </Card>

      <Modal
        title={`编辑工作流：${activeTemplate?.name || ''}`}
        open={editModalOpen}
        onCancel={() => setEditModalOpen(false)}
        onOk={handleSave}
        okText="保存"
        width={720}
      >
        {activeTemplate && (
          <>
            <Descriptions column={2} size="small" bordered style={{ marginBottom: 16 }}>
              <Descriptions.Item label="编码">{activeTemplate.code}</Descriptions.Item>
              <Descriptions.Item label="模块">{activeTemplate.module}</Descriptions.Item>
            </Descriptions>

            <div style={{ marginBottom: 12 }}>
              <Text strong>模板名称：</Text>
              <Input value={templateName} onChange={(e) => setTemplateName(e.target.value)} style={{ width: 300, marginLeft: 8 }} />
            </div>

            <div style={{ marginBottom: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Text strong>审批阶段配置</Text>
              <Button size="small" type="dashed" icon={<PlusOutlined />} onClick={addStage}>添加阶段</Button>
            </div>

            {stages.map((stage, idx) => (
              <Card key={stage.key} size="small" style={{ marginBottom: 8, background: '#fafafa' }}
                title={
                  <Space>
                    <Tag color="blue">第 {idx + 1} 阶段</Tag>
                    <Input size="small" value={stage.name} placeholder="阶段名称"
                      onChange={(e) => updateStage(stage.key, 'name', e.target.value)}
                      style={{ width: 160 }} />
                  </Space>
                }
                extra={
                  stages.length > 1 && (
                    <Popconfirm title="确认删除该阶段？" onConfirm={() => removeStage(stage.key)}>
                      <Button size="small" danger icon={<DeleteOutlined />}>删除</Button>
                    </Popconfirm>
                  )
                }
              >
                <Space wrap size="middle">
                  <div>
                    <Text type="secondary" style={{ fontSize: 12 }}>类型</Text><br />
                    <Select size="small" value={stage.type} style={{ width: 120 }}
                      onChange={(v) => updateStage(stage.key, 'type', v)}>
                      {STAGE_TYPES.map((t) => <Option key={t.value} value={t.value}>{t.label}</Option>)}
                    </Select>
                  </div>
                  {stage.type !== 'auto' && (
                    <div>
                      <Text type="secondary" style={{ fontSize: 12 }}>操作角色</Text><br />
                      <Select size="small" value={stage.role} style={{ width: 120 }}
                        onChange={(v) => updateStage(stage.key, 'role', v)}>
                        {ROLES.map((r) => <Option key={r.value} value={r.value}>{r.label}</Option>)}
                      </Select>
                    </div>
                  )}
                  {stage.type === 'auto' && (
                    <div>
                      <Text type="secondary" style={{ fontSize: 12 }}>自动动作</Text><br />
                      <Select size="small" value={stage.action} style={{ width: 160 }} placeholder="选择动作"
                        onChange={(v) => updateStage(stage.key, 'action', v)}>
                        {ACTIONS.map((a) => <Option key={a.value} value={a.value}>{a.label}</Option>)}
                      </Select>
                    </div>
                  )}
                  <div>
                    <Text type="secondary" style={{ fontSize: 12 }}>通知渠道</Text><br />
                    <Select size="small" mode="multiple" value={stage.notify ? stage.notify.split(',') : []} style={{ width: 200 }}
                      placeholder="选择通知渠道"
                      onChange={(v) => updateStage(stage.key, 'notify', v.join(','))}>
                      {NOTIFY_CHANNELS.map((c) => <Option key={c.value} value={c.value}>{c.label}</Option>)}
                    </Select>
                  </div>
                </Space>
              </Card>
            ))}

            {stages.length > 0 && (
              <div style={{ marginTop: 16 }}>
                <Text type="secondary">流程预览：</Text>
                <Steps size="small" current={-1} style={{ marginTop: 8 }}
                  items={stages.map((s) => ({
                    title: s.name || '未命名',
                    description: <span style={{ fontSize: 11 }}>
                      {STAGE_TYPES.find((t) => t.value === s.type)?.label}
                      {s.role && ` · ${ROLES.find((r) => r.value === s.role)?.label}`}
                    </span>,
                  }))}
                />
              </div>
            )}
          </>
        )}
      </Modal>
    </div>
  );
}
